<script lang="ts">
  // Moderation queue. Lists families awaiting review with the same 3D preview and parameter detail a
  // buyer will eventually see, and lets a moderator approve or reject each.
  //
  // Runs in both environments: inside Revit the host attaches its token, in the browser the session
  // cookie carries the same identity. The API enforces the Moderator role either way — this UI is
  // hidden from non-moderators only so nobody is shown a button that would fail.

  import { onMount } from "svelte";
  import { transport } from "@/transport";
  import { PICKER_TIMEOUT_MS } from "@/transport/types";
  import { categoryLabel, languageLabel, KNOWN_CATEGORIES, LOD_LEVELS } from "./categories";
  import FamilyViewer3D from "./FamilyViewer3D.svelte";
  import type {
    FamilyPackage,
    ModerationEditRequest,
    ModerationFamily,
    ModerationQueue,
    ModerationResult,
  } from "./types";
  import { AppButton, AppDialog, AppInput, AppSelect, AppState, AppTextarea } from "@/components/ui";
  import { t, locale } from "@/i18n/index.svelte";
  import type { SupportedLocale } from "@/i18n/index.svelte";

  let { onclose, onchanged }: { onclose?: () => void; onchanged?: () => void } = $props();

  type Status = "loading" | "ready" | "error";
  let status = $state<Status>("loading");
  let error = $state("");
  let items = $state<ModerationFamily[]>([]);

  // The slug currently being rejected (its reason box is open), and the pending reason text.
  let rejectingSlug = $state<string | null>(null);
  let rejectReason = $state("");
  // The slug currently being submitted, so its buttons disable without freezing the whole list.
  let busySlug = $state<string | null>(null);
  // The slug currently fetching a download URL — its own flag, since downloading must not disable
  // the approve/reject buttons the way a decision in flight does.
  let downloadingSlug = $state<string | null>(null);

  // The slug currently open for editing (its card-correction form is shown), and the form itself.
  // Comma-separated tags in the input, same convention as PublishDialog, split on save.
  let editingSlug = $state<string | null>(null);
  let editForm = $state({
    name: "",
    category: "",
    subcategory: "",
    omniClassCode: "",
    omniClassTitle: "",
    description: "",
    tags: "",
    lod: "" as string,
  });
  let editError = $state("");

  // The slug currently swapping in a corrected local file — Revit-only, its own flag for the same
  // reason downloadingSlug is: replacing must not disable the approve/reject/edit row of an
  // unrelated card, or freeze the one card's own row mid-pick (the file picker can sit open a while).
  let replacingSlug = $state<string | null>(null);

  interface DownloadTicket {
    available: boolean;
    url?: string | null;
  }

  /**
   * Opens the actual file behind a pending submission — not just its preview mesh — so a moderator
   * can judge it properly before deciding. `window.open` rather than a fetch: in the browser it is an
   * ordinary download, and inside Revit the same call is redirected to the system browser by
   * DominusWindow's NewWindowRequested handler, so no Revit-side file-saving code is needed at all.
   */
  async function download(family: ModerationFamily) {
    downloadingSlug = family.slug;
    error = "";
    try {
      const ticket = await transport.invoke<DownloadTicket>("GetModerationDownload", { slug: family.slug });
      if (ticket.available && ticket.url) window.open(ticket.url, "_blank");
      else error = t("moderation.downloadUnavailable");
    } catch (e) {
      error = messageOf(e);
    } finally {
      downloadingSlug = null;
    }
  }

  /**
   * Swaps this family's file for a corrected local one — Revit-only: PickFiles and
   * ExtractFamilyPackage both need a live Revit session. Reuses the same extraction the publish
   * flow uses, so the replacement goes through the identical year/hash detection; the server
   * verifies both again regardless.
   *
   * Reloads the whole queue on success rather than patching the row in place, the way saveEdit
   * does: a replace can change AvailableYears/SizeBytes/Build/Author, all of which are cheaper to
   * refetch than to recompute here.
   */
  async function replaceAsset(family: ModerationFamily) {
    replacingSlug = family.slug;
    error = "";
    try {
      const picked = await transport.invoke<{ paths: string[] }>(
        "PickFiles",
        { multiple: false },
        { timeoutMs: PICKER_TIMEOUT_MS },
      );
      const localPath = picked.paths?.[0];
      if (!localPath) return; // cancelled

      const pkg = await transport.invoke<FamilyPackage>("ExtractFamilyPackage", {
        localPath,
        includePreview: false,
      });
      if (!pkg.ok) {
        error = pkg.error ?? t("publish.extractFailed");
        return;
      }

      const res = await transport.invoke<ModerationResult>("ReplaceModerationAsset", {
        slug: family.slug,
        localPath: pkg.localPath,
        revitYear: pkg.revitYear,
        sha256: pkg.sha256,
      });
      if (res.ok) {
        await load();
      } else {
        error = res.error ?? t("moderation.replaceFailed");
      }
    } catch (e) {
      error = messageOf(e);
    } finally {
      replacingSlug = null;
    }
  }

  const isEmpty = $derived(status === "ready" && items.length === 0);

  // The category list always includes whatever the family already carries, even if it is not in
  // the curated set — same reasoning as PublishDialog's categoryOptions.
  const editCategoryOptions = $derived.by(() => {
    const set = new Set<string>(KNOWN_CATEGORIES);
    if (editForm.category) set.add(editForm.category);
    return [...set];
  });

  async function load() {
    status = "loading";
    error = "";
    try {
      const queue = await transport.invoke<ModerationQueue>("GetModerationQueue", { status: "pending" });
      items = queue.items ?? [];
      status = "ready";
    } catch (e) {
      error = messageOf(e);
      status = "error";
    }
  }

  async function approve(family: ModerationFamily) {
    busySlug = family.slug;
    error = "";
    try {
      const res = await transport.invoke<ModerationResult>("ApproveFamily", { slug: family.slug });
      if (res.ok) {
        remove(family.slug);
        onchanged?.();
      } else {
        error = res.error ?? t("moderation.actionFailed");
      }
    } catch (e) {
      error = messageOf(e);
    } finally {
      busySlug = null;
    }
  }

  function startReject(family: ModerationFamily) {
    cancelEdit();
    rejectingSlug = family.slug;
    rejectReason = "";
  }

  function cancelReject() {
    rejectingSlug = null;
    rejectReason = "";
  }

  async function confirmReject(family: ModerationFamily) {
    busySlug = family.slug;
    error = "";
    try {
      const res = await transport.invoke<ModerationResult>("RejectFamily", {
        slug: family.slug,
        reason: rejectReason.trim() || null,
      });
      if (res.ok) {
        cancelReject();
        remove(family.slug);
        onchanged?.();
      } else {
        error = res.error ?? t("moderation.actionFailed");
      }
    } catch (e) {
      error = messageOf(e);
    } finally {
      busySlug = null;
    }
  }

  function remove(slug: string) {
    items = items.filter((f) => f.slug !== slug);
  }

  function startEdit(family: ModerationFamily) {
    cancelReject();
    editingSlug = family.slug;
    editError = "";
    editForm = {
      name: family.name,
      category: family.revitCategory,
      subcategory: family.subcategory ?? "",
      omniClassCode: family.omniClassCode ?? "",
      omniClassTitle: family.omniClassTitle ?? "",
      description: family.description,
      tags: family.tags.join(", "),
      // Not just "truthy" — a family published before LOD had a fixed vocabulary (or edited
      // straight in the database) can carry a value outside LOD_LEVELS entirely. Falling back to
      // "" for any of those forces an explicit, valid pick instead of silently resubmitting
      // whatever was already there and failing the same server-side check a moment later.
      lod: LOD_LEVELS.includes(family.lod) ? String(family.lod) : "",
    };
  }

  function cancelEdit() {
    editingSlug = null;
    editError = "";
  }

  // Clears a failed save's error the moment the form is touched again, rather than leaving it
  // sitting there once the user has already fixed whatever it complained about — e.g. picking a
  // valid LOD after "must be one of 100, 200, ...". Reading every field is what makes the effect
  // depend on all of them; without that, changing e.g. the category would not re-run it.
  $effect(() => {
    void [
      editForm.name, editForm.category, editForm.subcategory, editForm.omniClassCode,
      editForm.omniClassTitle, editForm.description, editForm.tags, editForm.lod,
    ];
    editError = "";
  });

  async function saveEdit(family: ModerationFamily) {
    busySlug = family.slug;
    editError = "";
    try {
      const body: ModerationEditRequest = {
        slug: family.slug,
        name: editForm.name.trim(),
        revitCategory: editForm.category,
        subcategory: editForm.subcategory.trim() || null,
        omniClassCode: editForm.omniClassCode.trim() || null,
        omniClassTitle: editForm.omniClassTitle.trim() || null,
        description: editForm.description.trim(),
        tags: editForm.tags.split(",").map((s) => s.trim()).filter(Boolean),
        lod: Number(editForm.lod),
      };
      const res = await transport.invoke<ModerationResult>("EditModerationFamily", body);
      if (res.ok) {
        // Optimistic: the request just sent is exactly what the server now has, so patch the row
        // in place instead of reloading the whole queue over it.
        items = items.map((f) =>
          f.slug === family.slug
            ? {
                ...f,
                name: body.name,
                revitCategory: body.revitCategory,
                subcategory: body.subcategory,
                omniClassCode: body.omniClassCode,
                omniClassTitle: body.omniClassTitle,
                description: body.description,
                tags: body.tags,
                lod: body.lod,
              }
            : f,
        );
        cancelEdit();
      } else {
        editError = res.error ?? t("moderation.saveFailed");
      }
    } catch (e) {
      editError = messageOf(e);
    } finally {
      busySlug = null;
    }
  }

  function messageOf(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
  }

  function formatSize(bytes: number | null): string {
    if (bytes == null) return "—";
    if (bytes < 1024) return `${bytes} B`;
    const units = ["KB", "MB", "GB"];
    let value = bytes / 1024;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) {
      value /= 1024;
      i++;
    }
    return `${value.toFixed(1)} ${units[i]}`;
  }

  onMount(() => {
    void load();
  });
</script>

<AppDialog
  size="lg"
  title={t("moderation.title")}
  subtitle={t("moderation.subtitle")}
  closeLabel={t("publish.close")}
  onclose={() => onclose?.()}
>
  {#snippet actions()}
    <AppButton variant="secondary" size="sm" disabled={status === "loading"} onclick={load}>
      {t("moderation.refresh")}
    </AppButton>
  {/snippet}

  {#if status === "loading"}
    <AppState kind="loading">{t("moderation.loading")}</AppState>
  {:else if status === "error"}
    <AppState kind="error" retryLabel={t("common.retry")} onretry={load}>
      {error || t("moderation.loadFailed")}
    </AppState>
  {:else if isEmpty}
    <AppState kind="empty">{t("moderation.empty")}</AppState>
  {:else}
    {#if error}
      <p class="text-xs text-error text-center">{error}</p>
    {/if}

    {#each items as family (family.slug)}
      <!-- shrink-0 is load-bearing: without it, a flex-col container squeezes each row down to
           fit the available space instead of overflowing (the default for any flex child), so a
           queue with more than a couple of entries silently crushed every card to a sliver and
           clipped its own Approve/Reject/Download row via overflow-hidden — nothing to scroll to,
           because nothing was actually overflowing. Forcing rows to keep their natural height is
           what makes the dialog's own overflow-auto have something real to scroll. -->
      <div class="border border-line rounded-surface overflow-hidden flex flex-col sm:flex-row shrink-0">
        <div class="sm:w-56 shrink-0 aspect-video sm:aspect-auto bg-panel flex items-center justify-center">
          {#if family.meshUrl}
            <FamilyViewer3D meshUrl={family.meshUrl} cacheKey={family.slug} class="w-full h-full" />
          {:else}
            <span class="text-ink-muted text-xs px-4 text-center">{t("viewer.noGeometry")}</span>
          {/if}
        </div>

        <div class="flex-1 min-w-0 p-4 flex flex-col gap-2">
          <div>
            <h3 class="text-sm font-semibold truncate">
              {family.name}
              {#if family.isUpdate}
                <span class="ml-1 text-[11px] font-medium uppercase tracking-wide text-accent align-middle">
                  {t("moderation.update")} · v{family.semver}
                </span>
              {/if}
            </h3>
            <p class="text-xs text-ink-muted">
              {categoryLabel(family.revitCategory, locale.value as SupportedLocale)}
              · {t("moderation.by", { name: family.ownerName || "—" })}
            </p>
          </div>

          {#if editingSlug === family.slug}
            <!-- Card-correction form: the fields an author fills in at publish time, minus
                 identity and delivery (slug, kind, asset) — those are not a moderator's to touch. -->
            <div class="flex flex-col gap-2 mt-1">
              {#if editError}
                <p class="text-xs text-error">{editError}</p>
              {/if}

              <label class="flex flex-col gap-1">
                <span class="text-xs font-medium text-ink-muted">{t("publish.name")}</span>
                <AppInput bind:value={editForm.name} type="text" />
              </label>

              <label class="flex flex-col gap-1">
                <span class="text-xs font-medium text-ink-muted">{t("publish.category")}</span>
                <AppSelect bind:value={editForm.category}>
                  {#each editCategoryOptions as c (c)}
                    <option value={c}>{categoryLabel(c, locale.value as SupportedLocale)}</option>
                  {/each}
                </AppSelect>
              </label>

              <label class="flex flex-col gap-1">
                <span class="text-xs font-medium text-ink-muted">{t("publish.lod")}</span>
                <AppSelect bind:value={editForm.lod}>
                  <option value="" disabled>—</option>
                  {#each LOD_LEVELS as l (l)}
                    <option value={String(l)}>LOD {l}</option>
                  {/each}
                </AppSelect>
              </label>

              <label class="flex flex-col gap-1">
                <span class="text-xs font-medium text-ink-muted">{t("publish.subcategory")}</span>
                <AppInput bind:value={editForm.subcategory} type="text" placeholder={t("publish.subcategoryHint")} />
              </label>

              <div class="grid grid-cols-2 gap-2">
                <label class="flex flex-col gap-1">
                  <span class="text-xs font-medium text-ink-muted">{t("moderation.omniClassCode")}</span>
                  <AppInput bind:value={editForm.omniClassCode} type="text" />
                </label>
                <label class="flex flex-col gap-1">
                  <span class="text-xs font-medium text-ink-muted">{t("moderation.omniClassTitle")}</span>
                  <AppInput bind:value={editForm.omniClassTitle} type="text" />
                </label>
              </div>

              <label class="flex flex-col gap-1">
                <span class="text-xs font-medium text-ink-muted">{t("publish.description")}</span>
                <AppTextarea bind:value={editForm.description} rows={2} />
              </label>

              <label class="flex flex-col gap-1">
                <span class="text-xs font-medium text-ink-muted">{t("publish.tags")}</span>
                <AppInput bind:value={editForm.tags} type="text" placeholder={t("publish.tagsHint")} />
              </label>

              <div class="flex gap-2">
                <span
                  class="contents"
                  title={busySlug !== family.slug && !editForm.lod ? t("publish.lodRequired") : undefined}
                >
                  <AppButton
                    variant="primary"
                    size="sm"
                    disabled={busySlug === family.slug || !editForm.lod}
                    onclick={() => saveEdit(family)}
                  >
                    {busySlug === family.slug ? t("moderation.working") : t("moderation.save")}
                  </AppButton>
                </span>
                <AppButton variant="secondary" size="sm" disabled={busySlug === family.slug} onclick={cancelEdit}>
                  {t("moderation.cancel")}
                </AppButton>
              </div>
            </div>
          {:else}
            {#if family.description}
              <p class="text-xs text-ink-secondary line-clamp-2">
                {family.description}
              </p>
            {/if}

            <dl class="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-xs text-ink-muted">
              <dt>{t("detail.language")}</dt>
              <dd class="text-ink">{languageLabel(family.authoringLanguage)}</dd>
              <dt>{t("detail.units")}</dt>
              <dd class="text-ink">{family.unitSystem ?? "—"}</dd>
              <dt>{t("detail.revitYears")}</dt>
              <dd class="text-ink tabular-nums">{family.availableYears.join(", ") || "—"}</dd>
              <dt>{t("detail.parameters")}</dt>
              <dd class="text-ink">{family.parameters.length}</dd>
              <dt>{t("publish.lod")}</dt>
              <dd class="text-ink tabular-nums">{family.lod ? `LOD ${family.lod}` : "—"}</dd>
              <dt>{t("moderation.file")}</dt>
              <dd class="text-ink truncate" title={family.sourceFileName ?? undefined}>
                {family.sourceFileName ?? "—"} · {formatSize(family.sizeBytes)}
              </dd>
              <dt>{t("moderation.build")}</dt>
              <dd class="text-ink" class:font-mono={!!family.savedByBuild}>{family.savedByBuild ?? "—"}</dd>
              <dt>{t("moderation.author")}</dt>
              <dd class="text-ink truncate">{family.author ?? "—"}</dd>
              <dt>{t("moderation.lastSavedBy")}</dt>
              <dd class="text-ink truncate">{family.lastSavedBy ?? "—"}</dd>
            </dl>

            <!-- Reject reason box -->
            {#if rejectingSlug === family.slug}
              <div class="flex flex-col gap-2 mt-1">
                <AppTextarea bind:value={rejectReason} rows={2} placeholder={t("moderation.reasonHint")} />
                <div class="flex gap-2">
                  <AppButton variant="danger" size="sm" disabled={busySlug === family.slug} onclick={() => confirmReject(family)}>
                    {t("moderation.confirmReject")}
                  </AppButton>
                  <AppButton variant="secondary" size="sm" disabled={busySlug === family.slug} onclick={cancelReject}>
                    {t("moderation.cancel")}
                  </AppButton>
                </div>
              </div>
            {:else}
              <!-- Decision buttons -->
              <div class="flex gap-2 mt-1">
                <AppButton variant="primary" size="sm" disabled={busySlug === family.slug} onclick={() => approve(family)}>
                  {busySlug === family.slug ? t("moderation.working") : t("moderation.approve")}
                </AppButton>
                <AppButton variant="danger" size="sm" disabled={busySlug === family.slug} onclick={() => startReject(family)}>
                  {t("moderation.reject")}
                </AppButton>
                <AppButton variant="secondary" size="sm" disabled={busySlug === family.slug} onclick={() => startEdit(family)}>
                  {t("moderation.edit")}
                </AppButton>
                <!-- The actual file, not just the preview mesh above — for judging whether a submission
                     is put together properly before deciding on it. -->
                <AppButton variant="secondary" size="sm" disabled={downloadingSlug === family.slug} onclick={() => download(family)}>
                  {downloadingSlug === family.slug ? t("moderation.working") : t("moderation.download")}
                </AppButton>
                {#if transport.caps.revit}
                  <!-- Revit-only: needs a live session to pick and re-extract a local file. Downloaded,
                       fixed, and put back — the alternative to bouncing the submission with a rejection
                       over a mistake this fixes directly. -->
                  <AppButton
                    variant="secondary"
                    size="sm"
                    disabled={replacingSlug === family.slug}
                    onclick={() => replaceAsset(family)}
                  >
                    {replacingSlug === family.slug ? t("moderation.working") : t("moderation.replaceFile")}
                  </AppButton>
                {/if}
              </div>
            {/if}
          {/if}
        </div>
      </div>
    {/each}
  {/if}
</AppDialog>
